/**
 * Carga manual de un dato: el reemplazo de editar la celda del sheet.
 *
 * Es **uno solo** para todo el panel. Había tres —el del Overview, el de
 * Clientes y el de Revenue— pegándole al mismo endpoint por el mismo hook pero
 * diciendo cosas distintas, validando distinto y ofreciendo Reintentar con
 * criterios distintos: el mismo acto se veía y se comportaba según desde qué
 * pantalla se disparaba. Lo que de verdad cambia entre pantallas son tres cosas,
 * y están acá como props: si se puede cargar un mes que no es el de la pantalla,
 * en qué unidad se pide el número, y qué ficha de la métrica se muestra arriba
 * del campo.
 *
 * Escribe por el **mismo endpoint de ingesta** que van a usar los adapters de
 * Stripe, Plane, la plataforma y Search Console. Lo único que cambia es el campo
 * `source`, que la API fija en `manual` para lo que entra por acá: el badge de
 * proveniencia tiene que decir la verdad de dónde salió el número, aunque la
 * métrica declare otra fuente.
 *
 * Si el POST falla el diálogo **no se cierra**: muestra el mensaje de la API y
 * ofrece Reintentar con el valor intacto.
 */
import { formatPeriodo, type Unit } from '@ops/contracts'
import { useEffect, useState, type ReactNode } from 'react'

import { mensajeDeError } from '../../lib/api'
// `leerNumero` y no un parseo propio: distingue **vacío** de **ilegible**, que
// son dos avisos distintos, y lee es-AR (`$74.233`, `21,7`) igual que las celdas
// de Revenue. Los tres formularios escribían por el mismo endpoint, así que lo
// que se acepta tiene que ser lo mismo.
import { leerNumero } from '../../lib/numeros'
import { useCreateDatapoint } from '../../lib/queries'
import { useToast } from '../../shell'
import { Button, Modal, Stack, TextField } from '../../ui'

/** Cómo se pide el número, para que nadie cargue 21 donde iba 0,21. */
const UNIDAD_LABEL: Readonly<Record<Unit, string>> = {
  usd: 'en dólares',
  percent: 'en porcentaje',
  count: 'en unidades',
  months: 'en meses',
  ratio: 'como ratio, ej. 3,2',
  position: 'como posición',
}

/** Una fila de la ficha de la métrica que se muestra arriba del campo. */
export interface FilaFicha {
  rotulo: string
  valor: ReactNode
}

export interface CargarDatoDialogProps {
  open: boolean
  onClose: () => void
  /** Slug de la métrica en el catálogo. */
  slug: string
  /** Nombre de la métrica, para el título y el toast. */
  nombre: string
  /** Período de la pantalla. */
  periodo: string
  /**
   * Unidad en la que se pide el número. Sin esto el label dice "Valor" pelado y
   * nadie sabe si 21 es 21% o 0,21.
   */
  unidad?: Unit
  /** Permite cargar un mes que no es el de la pantalla (solo el Overview). */
  permitirCambiarPeriodo?: boolean
  /** Ayuda bajo el campo, ej. el benchmark de la métrica. */
  ayudaValor?: string
  /** Ficha de la métrica: fórmula, benchmark, definición, valor de hoy. */
  ficha?: readonly FilaFicha[]
}

export function CargarDatoDialog({
  open,
  onClose,
  slug,
  nombre,
  periodo,
  unidad,
  permitirCambiarPeriodo = false,
  ayudaValor,
  ficha,
}: CargarDatoDialogProps) {
  const cargar = useCreateDatapoint(slug)
  const toast = useToast()

  const [texto, setTexto] = useState('')
  const [nota, setNota] = useState('')
  const [mes, setMes] = useState(periodo)
  const [tocado, setTocado] = useState(false)

  // Al cambiar de métrica o de período el formulario arranca limpio: cargar el
  // churn con el número que alguien tipeó para el NPS es peor que no cargar nada.
  useEffect(() => {
    setTexto('')
    setNota('')
    setMes(periodo)
    setTocado(false)
    cargar.reset()
    // Depende solo de la métrica y el período a propósito: `cargar` cambia de
    // identidad en cada render, así que incluirlo limpiaría lo que se tipea.
  }, [slug, periodo, cargar.reset])

  const leido = leerNumero(texto)
  const valor = leido.ok && leido.valor !== null ? leido.valor : null

  function guardar() {
    setTocado(true)
    if (valor === null) return
    cargar.mutate(
      {
        periodo: mes,
        valor,
        ...(nota.trim() === '' ? {} : { nota: nota.trim() }),
      },
      {
        onSuccess: () => {
          toast.push('ok', `Se cargó ${nombre} de ${formatPeriodo(mes)}.`)
          onClose()
        },
      },
    )
  }

  const errorApi = cargar.isError ? mensajeDeError(cargar.error) : null
  // Vacío e ilegible son dos avisos distintos: "no escribiste nada" y "eso no es
  // un número" mandan a hacer cosas distintas.
  const avisoLocal = !tocado
    ? null
    : !leido.ok
      ? 'Escribí un número. Se acepta formato es-AR: 74.233 o 21,7.'
      : leido.valor === null
        ? 'Falta el valor: sin número no hay dato que cargar.'
        : null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Cargar ${nombre}`}
      subtitle={`Período ${formatPeriodo(mes)} · queda registrado como carga manual, con tu nombre y la fecha de hoy`}
      size="sm"
      error={errorApi}
      onRetry={errorApi === null ? undefined : guardar}
      reintentando={cargar.isPending}
      footer={
        <>
          <Button variante="sutil" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variante="primario"
            onClick={guardar}
            cargando={cargar.isPending}
          >
            Guardar el dato
          </Button>
        </>
      }
    >
      <Stack gap="sm">
        {ficha !== undefined && ficha.length > 0 ? (
          <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-meta">
            {ficha.map((fila) => (
              <div key={fila.rotulo} className="contents">
                <dt className="rotulo">{fila.rotulo}</dt>
                <dd className="min-w-0 text-text">{fila.valor}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {/* `inputMode="decimal"` y no `type="number"`: el número se lee con
            `leerNumero`, que acepta es-AR. Con type=number el navegador
            impondría su propia interpretación y rechazaría la coma. */}
        <TextField
          label={unidad === undefined ? 'Valor' : `Valor — ${UNIDAD_LABEL[unidad]}`}
          requerido
          value={texto}
          onChange={setTexto}
          inputMode="decimal"
          placeholder="Escribí el número del período"
          {...(ayudaValor === undefined ? {} : { ayuda: ayudaValor })}
          {...(avisoLocal === null ? {} : { error: avisoLocal })}
        />

        {permitirCambiarPeriodo ? (
          <TextField
            label="Período"
            ayuda="Formato YYYY-MM."
            value={mes}
            onChange={setMes}
          />
        ) : null}

        <TextField
          label="Nota (opcional)"
          ayuda="De dónde salió el número. Es lo que hace auditable una carga manual."
          value={nota}
          onChange={setNota}
          filas={3}
          maxLength={500}
        />
      </Stack>
    </Modal>
  )
}
